import type { Draft } from 'immer'
import {
  ALLIANZ_GOVERNMENT_ID_TYPES,
  ALLIANZ_ID_TYPE_DUNS,
  ALLIANZ_ID_TYPE_EIN,
  ALLIANZ_ID_TYPE_FEIN,
  ALLIANZ_ID_TYPE_LEI,
  ALLIANZ_ID_TYPE_VAT,
  type AllianzGovernmentIdTypeCode
} from '@klear/allianz/constants'
import type { AllianzCompanySummary } from '@klear/allianz/schema'
import { isValidLei } from '@klear/constants/lei'
import { VALID_COUNTRY_CODES, type CountryCode } from '@klear/constants/countries'
import type { OrganizationCreateWire } from '@klear/organization/organization.wire'
import { createEntityFormStateHelpers } from '@klear/ui/entity-form-state'

function identifierValue(company: AllianzCompanySummary, idTypeCode: string): string | undefined {
  return company.identifiers.find((id) => id.idTypeCode === idTypeCode)?.idValue
}

type AllianzOrgWireSlice = Pick<
  OrganizationCreateWire,
  'name' | 'dunsNumber' | 'lei' | 'ein' | 'vatNumber' | 'address' | 'size'
>

type AllianzOrgAddressWire = Pick<
  NonNullable<AllianzOrgWireSlice['address']>,
  'country' | 'line1' | 'city' | 'state' | 'postalCode'
>

export type AllianzOrgFormValues = {
  [K in keyof Omit<AllianzOrgWireSlice, 'address' | 'size'>]-?: string
} & {
  address: {
    [K in keyof AllianzOrgAddressWire]-?: K extends 'country' ? NonNullable<AllianzOrgAddressWire[K]> : string
  }
  size?: AllianzOrgWireSlice['size']
  allianzEhid?: string
}

export type AllianzOrgFormFieldPath =
  | Exclude<keyof AllianzOrgFormValues, 'address'>
  | `address.${Extract<keyof AllianzOrgFormValues['address'], string>}`

export type AllianzCompanyFormProposal = {
  path: AllianzOrgFormFieldPath
  label: string
  next: string
}

export type AllianzCompanyFormOverwrite = AllianzCompanyFormProposal & {
  current: string
}

const FIELD_LABELS: Record<AllianzOrgFormFieldPath, string> = {
  name: 'Name',
  allianzEhid: 'Allianz EHID',
  dunsNumber: 'DUNS',
  lei: 'LEI',
  ein: 'EIN',
  vatNumber: 'VAT',
  'address.country': 'Country',
  'address.line1': 'Address line 1',
  'address.city': 'City',
  'address.state': 'State',
  'address.postalCode': 'Postal code',
  size: 'Size'
}

export type ApplyAllianzCompanyOptions = {
  includeAllianzEhid?: boolean
}

const { setField: setAllianzOrgFormField } = createEntityFormStateHelpers<AllianzOrgFormValues, Record<string, never>>()

function formFieldString(values: AllianzOrgFormValues, path: AllianzOrgFormFieldPath): string {
  if (path.startsWith('address.')) {
    const key = path.slice('address.'.length) as keyof AllianzOrgFormValues['address']
    return values.address[key] ?? ''
  }
  const value = values[path as Exclude<keyof AllianzOrgFormValues, 'address'>]
  return value ?? ''
}

function proposal(path: AllianzOrgFormFieldPath, next: string | undefined): AllianzCompanyFormProposal | undefined {
  const value = next?.trim()
  if (!value) return undefined
  return { path, label: FIELD_LABELS[path], next: value }
}

export function proposedAllianzCompanyFields(
  company: AllianzCompanySummary,
  options: ApplyAllianzCompanyOptions = {}
): AllianzCompanyFormProposal[] {
  const includeAllianzEhid = options.includeAllianzEhid !== false
  const country = company.countryCode?.trim().toUpperCase()
  const lei = identifierValue(company, ALLIANZ_ID_TYPE_LEI)
  const proposals = [
    proposal('name', company.companyName),
    includeAllianzEhid ? proposal('allianzEhid', company.companyId) : undefined,
    proposal('dunsNumber', company.dunsNumber ?? identifierValue(company, ALLIANZ_ID_TYPE_DUNS)),
    proposal('lei', lei && isValidLei(lei) ? lei : undefined),
    proposal('ein', identifierValue(company, ALLIANZ_ID_TYPE_EIN) ?? identifierValue(company, ALLIANZ_ID_TYPE_FEIN)),
    proposal('vatNumber', identifierValue(company, ALLIANZ_ID_TYPE_VAT)),
    country && VALID_COUNTRY_CODES.has(country as CountryCode) ? proposal('address.country', country) : undefined,
    proposal('address.line1', company.line1),
    proposal('address.city', company.city),
    proposal('address.state', company.state),
    proposal('address.postalCode', company.postalCode),
    proposal('size', company.organizationSize)
  ]
  return proposals.filter((item): item is AllianzCompanyFormProposal => item != null)
}

export function allianzCompanyFormOverwrites(
  values: AllianzOrgFormValues,
  company: AllianzCompanySummary,
  options: ApplyAllianzCompanyOptions = {}
): AllianzCompanyFormOverwrite[] {
  const overwrites: AllianzCompanyFormOverwrite[] = []
  for (const item of proposedAllianzCompanyFields(company, options)) {
    const current = formFieldString(values, item.path).trim()
    if (!current || current === item.next) continue
    overwrites.push({ ...item, current })
  }
  return overwrites
}

export function lookupIdentifierPrefillFromOrgForm(values: AllianzOrgFormValues): {
  idTypeCode: AllianzGovernmentIdTypeCode | ''
  idValue: string
} {
  if (values.dunsNumber.trim()) {
    return { idTypeCode: ALLIANZ_ID_TYPE_DUNS, idValue: values.dunsNumber.trim() }
  }
  if (values.lei.trim()) {
    return { idTypeCode: ALLIANZ_ID_TYPE_LEI, idValue: values.lei.trim() }
  }
  if (values.ein.trim()) {
    return { idTypeCode: ALLIANZ_ID_TYPE_EIN, idValue: values.ein.trim() }
  }
  if (values.vatNumber.trim()) {
    return { idTypeCode: ALLIANZ_ID_TYPE_VAT, idValue: values.vatNumber.trim() }
  }
  return { idTypeCode: '', idValue: '' }
}

export function formatAllianzOverwriteDescription(overwrites: AllianzCompanyFormOverwrite[]): string {
  const parts = overwrites.map((item) => `${item.label} ("${item.current}" → "${item.next}")`)
  return `Applying this company match will overwrite existing values: ${parts.join('; ')}.`
}

export function applyAllianzCompanyToForm<T extends AllianzOrgFormValues>(
  draft: Draft<T>,
  company: AllianzCompanySummary,
  options: ApplyAllianzCompanyOptions = {}
): AllianzOrgFormFieldPath[] {
  const dirty: AllianzOrgFormFieldPath[] = []
  for (const item of proposedAllianzCompanyFields(company, options)) {
    setAllianzOrgFormField(draft as Draft<AllianzOrgFormValues>, item.path, item.next)
    dirty.push(item.path)
  }
  return dirty
}

export const ALLIANZ_GOVERNMENT_ID_TYPE_OPTIONS = ALLIANZ_GOVERNMENT_ID_TYPES.map((t) => ({
  value: t.idTypeCode,
  label: t.label
}))
